import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { SHIPMENT_TOAST_MESSAGES } from "~/features/shipment-management/constants/toast-messages.ts";
import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";

export const postMarkUndelivered = async (poNumber: string) => {
	const resp = await postAndValidate(
		apiRoutes.POSTMARKUNDELIVERED.replace(":poNumber", poNumber),
		null,
		z.unknown(),
	);

	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const usePostMarkUndelivered = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useShipmentNotifications();

	const { mutate, mutateAsync, isPending, error, data, reset } = useMutation({
		mutationKey: ["markUndelivered"],
		mutationFn: (poNumber: string) => postMarkUndelivered(poNumber),
		onSuccess: () => {
			showSuccess(SHIPMENT_TOAST_MESSAGES.shipmentUnmarkedDelivered);
			// Keep the mutation pending until the shipment refetches with the new state.
			return queryClient.invalidateQueries({ queryKey: ["shipment"] });
		},
		onError: (err) => {
			showError(
				err instanceof Error
					? err.message
					: "Failed to unmark shipment as delivered. Please try again.",
			);
		},
	});

	return {
		markUndelivered: mutate,
		markUndeliveredAsync: mutateAsync,
		isSavingMarkUndelivered: isPending,
		error,
		data,
		resetError: reset,
	};
};
